import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Table, Form, Pagination, Badge, Modal, Button } from "react-bootstrap";
import TableWrapper from "../../components/TableWrapper";
import { useStores } from "../../context/mobx-context";
import "./BeautifyReviews.css";

const BeautifyReviews = observer(() => {
  const { beautifyStore } = useStores();
  const [showModal, setShowModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    beautifyStore.fetchReviews();
  }, [beautifyStore, beautifyStore.currentPage, beautifyStore.pageSize, beautifyStore.filterScore]);

  const handlePageChange = (page) => {
    beautifyStore.setCurrentPage(page - 1);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    beautifyStore.setFilterScore(value === "" ? null : parseInt(value));
  };

  const handleShowImages = (review) => {
    setSelectedReview(review);
    setShowModal(true);
  };

  const renderStars = (score) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= score ? "star filled" : "star"}>
          ★
        </span>
      );
    }
    return <div className="star-rating">{stars}</div>;
  };

  const renderStatus = (status) => {
    const statusMap = {
      pending: { variant: "warning", label: "Pending" },
      accepted: { variant: "success", label: "Accepted" },
      rejected: { variant: "danger", label: "Rejected" },
      completed: { variant: "info", label: "Completed" },
    };
    
    const statusInfo = statusMap[status] || { variant: "secondary", label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const totalPages = Math.ceil(beautifyStore.totalReviews / beautifyStore.pageSize);

  return (
    <div className="page-container">
      <TableWrapper
        title="Beautify Reviews"
        showLoading={beautifyStore.isFetching}
      >
        <div className="filters-container mb-3">
          <Form.Group className="filter-group">
            <Form.Label>Filter by Rating</Form.Label>
            <Form.Select onChange={handleFilterChange} value={beautifyStore.filterScore || ""}>
              <option value="">All Ratings</option>
              <option value="1">1 Star</option>
              <option value="2">2 Stars</option>
              <option value="3">3 Stars</option>
              <option value="4">4 Stars</option>
              <option value="5">5 Stars</option>
            </Form.Select>
          </Form.Group>
        </div>

        <Table className="model-table beautify-reviews-table">
          <thead>
            <tr>
              <th>Review ID</th>
              <th>User ID</th>
              <th>Piece ID</th>
              <th>Rating</th>
              <th>Comment</th>
              <th>Status</th>
              <th>Created</th>
              <th>Images</th>
            </tr>
          </thead>
          <tbody>
            {beautifyStore.reviews.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center">
                  No reviews found
                </td>
              </tr>
            ) : (
              beautifyStore.reviews.map((review) => (
                <tr key={review.id}>
                  <td className="review-id">{review.id}</td>
                  <td>{review.user_id}</td>
                  <td>{review.piece_id}</td>
                  <td>{renderStars(review.review_score)}</td>
                  <td className="review-description">
                    {review.review_description || <span className="text-muted">No comment</span>}
                  </td>
                  <td>{renderStatus(review.status)}</td>
                  <td>{new Date(review.created_at).toLocaleDateString()}</td>
                  <td>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => handleShowImages(review)}
                    >
                      View Images
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="pagination-container">
            <Pagination>
              <Pagination.First 
                onClick={() => handlePageChange(1)} 
                disabled={beautifyStore.currentPage === 0} 
              />
              <Pagination.Prev 
                onClick={() => handlePageChange(beautifyStore.currentPage)} 
                disabled={beautifyStore.currentPage === 0} 
              />
              
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index}
                  active={beautifyStore.currentPage === index}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next 
                onClick={() => handlePageChange(beautifyStore.currentPage + 2)} 
                disabled={beautifyStore.currentPage === totalPages - 1} 
              />
              <Pagination.Last 
                onClick={() => handlePageChange(totalPages)} 
                disabled={beautifyStore.currentPage === totalPages - 1} 
              />
            </Pagination>
          </div>
        )}
      </TableWrapper>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Image Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReview && (
            <div className="image-comparison-container">
              <div className="image-section">
                <h5>Original Image</h5>
                <img 
                  src={selectedReview.piece_image_url} 
                  alt="Original piece" 
                  className="comparison-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/placeholder-image.png";
                  }}
                />
              </div>
              <div className="image-section">
                <h5>Beautified Image</h5>
                {selectedReview.beautified_image_url ? (
                  <img 
                    src={selectedReview.beautified_image_url} 
                    alt="Beautified piece" 
                    className="comparison-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-image.png";
                    }}
                  />
                ) : (
                  <div className="no-image-placeholder">
                    <span>No beautified image available</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default BeautifyReviews;